import { ISegmentDatasourceColumnState } from 'src/app/dashboards/common/items/filter/segment-filter/segments';
import { GcpProjectId } from '../gcp-project-id.model';
import { ICompiledSQLTokens } from './datasource';

interface SegmentGroup {
    name: string;
    values: string[];
    alias: string;
}

/** Compiles filters and splits using one TVF call per segment group. */
export class SegmentSqlTokensCompiler {
    /** (selected_group, selected_values) -> (segment_player_id, segment). */
    private static readonly groupTvf =
        'bi_config.TABLE_GET_SEGMENT_GROUP_PLAYERS';
    private static readonly optionSeparator = ': ';

    public static compileTokens(
        state: ISegmentDatasourceColumnState,
        options: { gcpProjectId: GcpProjectId }
    ): Partial<ICompiledSQLTokens> {
        if (state.isExcludeApplied && state.isSplitApplied) {
            throw new Error(
                'Split is not supported for exclude mode in segment filter'
            );
        }
        if (!state.isFilterApplied && !state.isSplitApplied) {
            return {};
        }

        const groups = this.getGroups(
            state.value.options,
            state.isExcludeApplied ? 'seg_excl' : 'seg'
        );
        // A player has one value per group, so AND over two values of one group matches nobody.
        const matchesNobody =
            groups.length === 0 ||
            (state.value.operator === 'AND' &&
                groups.some(({ values }) => values.length > 1));
        if (matchesNobody) {
            // No joins: a constant FALSE lets BigQuery skip the scan, a LEFT JOIN to an empty TVF does not.
            if (state.isExcludeApplied) {
                return {};
            }
            return state.isSplitApplied
                ? { where: ['FALSE'], split_group: ['CAST(NULL AS STRING)'] }
                : { where: ['FALSE'] };
        }

        const matched = groups
            .map(({ alias }) => `${alias}.segment_player_id IS NOT NULL`)
            .join(` ${state.value.operator} `);
        const tokens: Partial<ICompiledSQLTokens> = {
            join: groups.map((group) =>
                this.buildGroupJoinSQL(group, options.gcpProjectId)
            ),
            where: [
                state.isExcludeApplied ? `NOT (${matched})` : `(${matched})`,
            ],
        };

        if (state.isSplitApplied) {
            const segments = groups.map(({ alias }) => `${alias}.segment`);
            tokens.split_group = [
                segments.length === 1
                    ? segments[0]
                    : `ARRAY_TO_STRING([${segments.join(', ')}], ', ')`,
            ];
        }
        return tokens;
    }

    private static getGroups(
        options: readonly string[],
        aliasPrefix: string
    ): SegmentGroup[] {
        const valuesByGroup = new Map<string, string[]>();
        // Match the full-label order of the container TVF when composing splits.
        for (const option of [...options].sort()) {
            const separatorIndex = option.indexOf(this.optionSeparator);
            // A second separator makes the group ambiguous; a wrong split would silently match nobody.
            if (
                separatorIndex < 0 ||
                option.lastIndexOf(this.optionSeparator) !== separatorIndex
            ) {
                throw new Error(
                    `Invalid segment option '${option}'. Expected '<group>: <value>' with a single '${this.optionSeparator}'.`
                );
            }
            const name = option.slice(0, separatorIndex);
            const value = option.slice(
                separatorIndex + this.optionSeparator.length
            );
            const values = valuesByGroup.get(name) ?? [];
            values.push(value);
            valuesByGroup.set(name, values);
        }
        return [...valuesByGroup].map(([name, values], index) => ({
            name,
            values,
            alias: `${aliasPrefix}_${index + 1}`,
        }));
    }

    private static buildGroupJoinSQL(
        group: SegmentGroup,
        gcpProjectId: GcpProjectId
    ): string {
        const { name, values, alias } = group;
        const valuesSQL = values.map(this.toSqlStringLiteral).join(', ');

        return `LEFT JOIN \`${gcpProjectId}.${this.groupTvf}\`(${this.toSqlStringLiteral(name)}, ARRAY<STRING>[${valuesSQL}]) ${alias}
                ON ${alias}.segment_player_id = ds.player_dim.player_id`;
    }

    /** GoogleSQL string literal; escapes backslashes and single quotes. */
    private static toSqlStringLiteral(value: string): string {
        return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
    }
}
